import {
    DisplayTestStatus,
    TestResultStatus,
    type TestAttachment,
    type TestResult as RawTestResult,
    type TestSpec as RawTestSpec,
    type TestSuite as RawTestSuite,
} from "../types/TestCasesTab.types";

export class TestResult {
    constructor(
        public readonly status: TestResultStatus | undefined,
        public readonly duration: number,
        public readonly attachments: TestAttachment[] = [],
        public readonly startTime?: string,
        public readonly errors?: unknown[],
    ) {}

    static fromRaw(raw: RawTestResult): TestResult {
        // Playwright's official type is `TestStatus | undefined` with values
        // 'passed' | 'failed' | 'timedOut' | 'skipped' | 'interrupted'. The
        // dashboard enum covers the first four; 'interrupted' falls through to
        // Unknown in displayStatus (intentional — no UI affordance for it).
        return new TestResult(
            raw.status as TestResultStatus | undefined,
            raw.duration,
            raw.attachments,
            raw.startTime,
            raw.errors,
        );
    }

    get hasVideo(): boolean {
        return this.attachments.some((a) => a.contentType === "video/webm" && a.name === "video");
    }

    get videoAttachment(): TestAttachment | undefined {
        const videos = this.attachments.filter((a) => a.contentType === "video/webm" && a.name === "video");
        return videos.length > 0 ? videos[videos.length - 1] : undefined;
    }
}

export interface TestAnnotation {
    type: string;
    description: string;
}

export class TestCase {
    constructor(
        public readonly title: string,
        public readonly id: string,
        public readonly file: string,
        public readonly line: number,
        public readonly results: TestResult[],
        public readonly ok = true,
        public readonly tags: string[] = [],
        public readonly annotations: TestAnnotation[] = [],
        public readonly description?: string,
    ) {}

    static fromRaw(raw: RawTestSpec): TestCase {
        const results = raw.tests[0]?.results.map((r) => TestResult.fromRaw(r)) ?? [];
        const rawAnnotations: TestAnnotation[] = (raw.tests[0]?.annotations ?? []).map((a) => ({
            type: a.type,
            description: a.description ?? "",
        }));
        const description = rawAnnotations.find((a) => a.type === "description")?.description;
        const titleFormatted = raw.title.charAt(0).toUpperCase() + raw.title.slice(1);
        return new TestCase(titleFormatted, raw.id, raw.file, raw.line, results, raw.ok, raw.tags, rawAnnotations, description);
    }

    get displayStatus(): DisplayTestStatus {
        if (this.results.length === 0) return DisplayTestStatus.NoResults;
        const hasPassed = this.results.some((r) => r.status === TestResultStatus.Passed);
        const hasFailed = this.results.some((r) => r.status === TestResultStatus.Failed || r.status === TestResultStatus.TimedOut);
        // Flaky: eventually passed but had at least one failing attempt
        if (hasPassed && hasFailed) return DisplayTestStatus.Flaky;
        if (hasPassed) return DisplayTestStatus.Passed;
        switch (this.results[0].status) {
            case TestResultStatus.Failed:
            case TestResultStatus.TimedOut: return DisplayTestStatus.Failed;
            case TestResultStatus.Skipped: return DisplayTestStatus.Skipped;
            default: return DisplayTestStatus.Unknown;
        }
    }

    get hasVideo(): boolean {
        return this.results.some((r) => r.hasVideo);
    }

    get historyKey(): string {
        return `${this.file}:${this.title}`;
    }

    get duration(): number {
        if (this.results.length === 0) return 0;
        return this.results[this.results.length - 1].duration;
    }

    get primaryVideoPath(): string | undefined {
        return this.results.find((r) => r.hasVideo)?.videoAttachment?.path;
    }
}

export class TestSuite {
    constructor(
        public readonly title: string,
        public readonly file: string,
        public readonly line: number,
        public readonly testCases: TestCase[] = [],
        public readonly childSuites: TestSuite[] = [],
        public readonly level = 0,
    ) {}

    static fromRaw(raw: RawTestSuite, level = 0): TestSuite {
        const testCases = raw.specs.map((s) => TestCase.fromRaw(s));
        const childSuites = (raw.suites ?? []).map((s) => TestSuite.fromRaw(s, level + 1));
        return new TestSuite(raw.title, raw.file, raw.line, testCases, childSuites, level);
    }

    get totalTestCount(): number {
        return this.testCases.length + this.childSuites.reduce((n, s) => n + s.totalTestCount, 0);
    }

    get allTestCases(): TestCase[] {
        return [...this.testCases, ...this.childSuites.flatMap((s) => s.allTestCases)];
    }

    get isSeedFile(): boolean {
        return this.file.endsWith("/seed.spec.ts") || this.file.endsWith("\\seed.spec.ts") || this.title === "seed.spec.ts";
    }

    get isFileLevel(): boolean {
        return this.level === 0 && this.file.includes(".spec.");
    }
}

export class TestResultsModel {
    constructor(
        public readonly suites: TestSuite[],
        public readonly config?: unknown,
        public readonly errors?: unknown[],
    ) {}

    static fromRaw(raw: { suites: RawTestSuite[]; config?: unknown; errors?: unknown[] }): TestResultsModel {
        const suites = raw.suites
            .map((s) => TestSuite.fromRaw(s))
            .filter((s) => !s.isSeedFile);
        return new TestResultsModel(suites, raw.config, raw.errors);
    }

    get allTestCases(): TestCase[] {
        return this.suites.flatMap((s) => s.allTestCases);
    }

    get totalTestCount(): number {
        return this.suites.reduce((n, s) => n + s.totalTestCount, 0);
    }

    get passedCount(): number {
        return this.allTestCases.filter((t) => t.displayStatus === DisplayTestStatus.Passed).length;
    }

    get failedCount(): number {
        return this.allTestCases.filter((t) => t.displayStatus === DisplayTestStatus.Failed).length;
    }

    get flakyCount(): number {
        return this.allTestCases.filter((t) => t.displayStatus === DisplayTestStatus.Flaky).length;
    }

    get skippedCount(): number {
        return this.allTestCases.filter(
            (t) => t.displayStatus === DisplayTestStatus.Skipped || t.displayStatus === DisplayTestStatus.NoResults,
        ).length;
    }

    get displaySuites(): TestSuite[] {
        return this.suites.flatMap((suite) => {
            if (suite.isFileLevel && suite.testCases.length === 0 && suite.childSuites.length > 0) {
                return suite.childSuites;
            }
            return [suite];
        });
    }

    get groupedDisplaySuites(): TestSuite[] {
        const grouped = new Map<string, TestSuite[]>();
        for (const suite of this.displaySuites) {
            const bucket = grouped.get(suite.title);
            if (bucket) bucket.push(suite);
            else grouped.set(suite.title, [suite]);
        }

        const consolidated: TestSuite[] = [];
        grouped.forEach((group, title) => {
            if (group.length === 1) {
                consolidated.push(group[0]);
            } else {
                const uniqueFiles = new Set(group.map((s) => s.file));
                if (uniqueFiles.size > 1) {
                    const fileSubSuites = group.map((s) => {
                        const basename = s.file.replace(/\.(spec|test)\.[jt]sx?$/, "").split(/[/\\]/).pop() ?? s.title;
                        return new TestSuite(basename, s.file, s.line, s.testCases, s.childSuites, group[0].level + 1);
                    });
                    consolidated.push(new TestSuite(title, group[0].file, group[0].line, [], fileSubSuites, group[0].level));
                } else {
                    const allCases = group.flatMap((s) => s.testCases);
                    const allChildren = group.flatMap((s) => s.childSuites);
                    consolidated.push(new TestSuite(title, group[0].file, group[0].line, allCases, allChildren, group[0].level));
                }
            }
        });

        // Unwrap single wrapper describe
        if (consolidated.length === 1 && consolidated[0].childSuites.length > 0) {
            const wrapper = consolidated[0];
            const result: TestSuite[] = [];
            if (wrapper.testCases.length > 0) {
                result.push(new TestSuite("General", wrapper.file, wrapper.line, wrapper.testCases, [], wrapper.level));
            }
            result.push(...wrapper.childSuites);
            return result;
        }

        return consolidated;
    }
}
